/**
 * TroubleshootingModal — конкретная модалка «Не приходит пароль» страницы
 * PasswordRecovery (Authentication).
 *
 * Живёт в папке своей страницы, а НЕ в molecules и НЕ в job-файле: у неё
 * конкретный заголовок, тело и условие показа, которые имеют смысл только в
 * контексте восстановления пароля. Переиспользуемая — только молекула
 * `Modal` (форма отображения); её конкретное наполнение принадлежит странице.
 * См. `ui-storybook/.claude/memory/page_owned_modals_rule.md`.
 *
 * Light-контекст обеспечивает сама `Modal` (внутри неё
 * `SurfaceContextProvider value='light'`), поэтому `theme.palette.surface.*`
 * здесь резолвится в light-значения. `backdrop` кладёт панель оверлеем поверх
 * тёмного AuthShell.
 *
 * @format
 */

import {styled} from '@mui/material/styles';
import {Trans, useTranslation} from 'react-i18next';

import {Modal} from 'ui/components/molecules/Modal';
import {Button} from 'ui/components/atoms/Button';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';

export interface TroubleshootingModalProps {
  /** Показать модалку. При `false` возвращает `null`. */
  open: boolean;
  /** Закрыть (× или клик по backdrop). */
  onClose: () => void;
  /** «Попробовать ещё» — вернуться к вводу канала. */
  onRetry: () => void;
  /** «Связаться с поддержкой». */
  onContactSupport: () => void;
}

// Цвет НЕ задаём — наследуем от Body молекулы Modal (её `text.primary`).
// Modal сам управляет своей палитрой; наш контент должен совпадать с его
// собственным текстом, а не переопределять цвет своим значением.
const Paragraph = styled('p')({
  ...text('body.m'),
  margin: 0,
});

const TipList = styled('ul')(({theme}) => ({
  ...text('body.m'),
  margin: 0,
  paddingLeft: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

export function TroubleshootingModal({
  open,
  onClose,
  onRetry,
  onContactSupport,
}: TroubleshootingModalProps) {
  const {t} = useTranslation();

  if (!open) return null;

  return (
    <Modal
      backdrop
      variant='primary'
      icon='help'
      title={t('Не приходит пароль', 'Не приходит\u00A0пароль')}
      onClose={onClose}
      footerActions={
        <>
          <Button variant='ghost' color='neutral' onClick={onContactSupport}>
            {t('Связаться с поддержкой', 'Связаться с поддержкой')}
          </Button>
          <Button variant='solid' color='primary' onClick={onRetry}>
            {t('Попробовать ещё', 'Попробовать ещё')}
          </Button>
        </>
      }
    >
      <Stack gap='m'>
        <Paragraph>
          <Trans>
            Иногда пароль нужно подождать несколько минут. Но{'\u00A0'}если{'\u00A0'}он долго не приходит,
            вот что{'\u00A0'}можно{'\u00A0'}сделать:
          </Trans>
        </Paragraph>
        <TipList>
          <li>
            <Trans>
              <strong>Попробуйте ещё раз.</strong> Иногда сервис отправки{'\u00A0'}барахлит.
            </Trans>
          </li>
          <li>
            <Trans>
              <strong>Правильный канал.</strong> Сотрудники{'\u00A0'}— через{'\u00A0'}емейл, остальные{'\u00A0'}— по{'\u00A0'}номеру.
            </Trans>
          </li>
          <li>
            <Trans>
              <strong>Поддержка.</strong> Напишите нам, если{'\u00A0'}другие способы не{'\u00A0'}сработали.
            </Trans>
          </li>
        </TipList>
      </Stack>
    </Modal>
  );
}
